import { groupReferenceSubject } from '@apps/web/features/phoebe_chat_v3/model/group_reference_label';
import { groupModeLabel } from '@apps/web/lib/outreach_shift_ordering';
import {
    type SnapshotShift,
    type SnapshotUnit,
} from '@libraries/typescript/api_client/schema_types';

// Past five, a list of dates is no longer readable and the span is what the
// coordinator is reading for.
const MAX_LISTED_DAYS = 5;

export type ShiftWindow = Readonly<{
    readonly day: string;
    readonly window: string;
}>;

export type UnitHeaderParts = Readonly<{
    readonly title: string;
    readonly countAndRule: string;
    readonly shiftCount: number;
    readonly days: ReadonlyArray<string>;
    readonly daysAreSpan: boolean;
    readonly sharedTime: string | null;
    readonly perShiftTimes: ReadonlyArray<ShiftWindow>;
}>;

type ShiftClock = Readonly<{
    readonly start: string;
    readonly end: string;
    readonly endDay: string | null;
}>;

/**
 * The header's parts: who the unit's shifts are for, the rule binding them,
 * the days they fall on and the hours they run.
 *
 * One string cannot carry a title line, a meta line and a per-shift grid, so
 * the layout choice stays in the component and this hands it the pieces.
 */
export function unitHeader(
    unit: SnapshotUnit,
    organizationTimeZone: string,
    formatName: (fullName: string | null | undefined) => string,
): UnitHeaderParts {
    const shiftCount = unit.shifts.length;
    const clientNames = [
        ...new Set(
            unit.shifts
                .map((shift) => formatName(shift.client_name))
                .filter((name) => name.length > 0),
        ),
    ];
    const clocks = unit.shifts.map((shift) =>
        shiftClock(shift, organizationTimeZone),
    );
    const dayNames = unit.shifts.map((shift) =>
        formatDay(shift.shift_start_time, organizationTimeZone),
    );
    const daysAreSpan = dayNames.length > MAX_LISTED_DAYS;
    const sharedTime = sharedWindow(clocks);
    return {
        title: headerTitle(clientNames, shiftCount),
        countAndRule: countAndRule(unit, clientNames.length),
        shiftCount,
        days: daysAreSpan ? spanEnds(dayNames) : dayNames,
        daysAreSpan,
        sharedTime,
        perShiftTimes:
            sharedTime === null
                ? clocks.map((clock, at) => ({
                      day: dayNames[at] ?? '',
                      window: formatShiftWindow(clock),
                  }))
                : [],
    };
}

function headerTitle(
    clientNames: ReadonlyArray<string>,
    shiftCount: number,
): string {
    const [onlyClient] = clientNames;
    if (clientNames.length === 1 && onlyClient !== undefined) {
        return onlyClient;
    }
    return groupReferenceSubject({ clientNames, shiftCount });
}

function countAndRule(unit: SnapshotUnit, namedClients: number): string {
    const shiftCount = unit.shifts.length;
    if (shiftCount < 2) {
        return '';
    }
    const rule = groupModeLabel(unit.group_mode ?? 'any');
    // A title naming more than one client already states the count.
    return namedClients === 1 ? `${shiftCount} shifts · ${rule}` : rule;
}

function spanEnds(dayNames: ReadonlyArray<string>): ReadonlyArray<string> {
    return [dayNames[0] ?? '', dayNames[dayNames.length - 1] ?? ''];
}

/** The one window every shift runs, or null when they differ. */
function sharedWindow(clocks: ReadonlyArray<ShiftClock>): string | null {
    const [first] = clocks;
    if (first === undefined) {
        return null;
    }
    // Two 8 AM shifts on either side of a daylight-saving change hold
    // different offsets from UTC, so only the rendered local clock can say
    // whether the group runs one window.
    const same = clocks.every(
        (clock) =>
            clock.start === first.start &&
            clock.end === first.end &&
            (clock.endDay === null) === (first.endDay === null),
    );
    if (!same) {
        return null;
    }
    // Members of a group cross into different dates, so a shared overnight
    // window can only state the clock.
    return clocks.length === 1
        ? formatShiftWindow(first)
        : formatClockRange(first);
}

function shiftClock(
    shift: SnapshotShift,
    organizationTimeZone: string,
): ShiftClock {
    const time = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        timeZone: organizationTimeZone,
    });
    const startDay = formatDay(shift.shift_start_time, organizationTimeZone);
    const endDay = formatDay(shift.shift_end_time, organizationTimeZone);
    return {
        start: time.format(new Date(shift.shift_start_time)),
        end: time.format(new Date(shift.shift_end_time)),
        endDay: startDay === endDay ? null : endDay,
    };
}

/** One shift's own window, naming the date it runs into overnight. */
function formatShiftWindow(clock: ShiftClock): string {
    if (clock.endDay === null) {
        return formatClockRange(clock);
    }
    return `${clock.start} – ${clock.endDay}, ${clock.end}`;
}

/**
 * `Intl.DateTimeFormat.formatRange` does exactly this, but it is absent from
 * the ES2020 library this project compiles against, so the shared meridiem is
 * dropped by hand: "4:00 – 8:00 PM".
 */
function formatClockRange(clock: ShiftClock): string {
    const meridiem = clock.end.slice(-2);
    const openTime = clock.start.endsWith(meridiem)
        ? clock.start.slice(0, -3)
        : clock.start;
    return `${openTime} – ${clock.end}`;
}

function formatDay(isoTime: string, organizationTimeZone: string): string {
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: organizationTimeZone,
    }).format(new Date(isoTime));
}
